import { DescriptionCaret, DescriptionLine, normalizeRuns, runLength, splitRuns, TextRun } from './mobileDescription'

const cloneLine = (line: DescriptionLine): DescriptionLine => ({
  kind: line.kind,
  marker: line.marker,
  runs: line.runs.map((run) => ({ text: run.text, bold: run.bold }))
})

interface InsertInput {
  lines: DescriptionLine[]
  index: number
  offset: number
  pasted: DescriptionLine[]
}

const headingFrom = (current: DescriptionLine, incoming: DescriptionLine): DescriptionLine => ({
  kind: incoming.kind === 'bullet' ? 'bullet' : current.kind,
  marker: incoming.kind === 'bullet' ? incoming.marker : current.marker,
  runs: incoming.runs
})

const remainderLine = (current: DescriptionLine, right: TextRun[]): DescriptionLine => {
  if (current.kind === 'bullet' && runLength(right) > 0) {
    return { kind: 'bullet', marker: current.marker, runs: right }
  }
  return { kind: 'text', marker: current.marker, runs: right }
}

interface PastedParts {
  current: DescriptionLine
  left: TextRun[]
  right: TextRun[]
  chunks: DescriptionLine[]
}

const assemblePasted = ({ current, left, right, chunks }: PastedParts): DescriptionLine[] => {
  const [first] = chunks
  const [last] = chunks.slice(-1)
  if (chunks.length === 1 && first.kind !== 'rule') {
    return [{ ...headingFrom(current, first), runs: normalizeRuns([...left, ...first.runs, ...right]) }]
  }

  const assembled: DescriptionLine[] = []
  if (first.kind === 'rule') {
    if (runLength(left) > 0) {
      assembled.push({ kind: current.kind, marker: current.marker, runs: left })
    }
    assembled.push(first)
  } else {
    assembled.push({ ...headingFrom(current, first), runs: normalizeRuns([...left, ...first.runs]) })
  }

  if (chunks.length === 1) {
    assembled.push(remainderLine(current, right))
    return assembled
  }

  assembled.push(...chunks.slice(1, -1))
  if (last.kind === 'rule') {
    assembled.push(last, remainderLine(current, right))
    return assembled
  }
  assembled.push({ kind: last.kind, marker: last.marker, runs: normalizeRuns([...last.runs, ...right]) })
  return assembled
}

interface CaretParts {
  singleText: boolean
  left: TextRun[]
  first: DescriptionLine
  last: DescriptionLine
}

const pastedCaretOffset = ({ singleText, left, first, last }: CaretParts): number => {
  if (singleText) {
    return runLength(left) + runLength(first.runs)
  }
  if (last.kind === 'rule') {
    return 0
  }
  return runLength(last.runs)
}

interface InsertResult {
  lines: DescriptionLine[]
  caret: DescriptionCaret
}

export const insertLines = ({ lines, index, offset, pasted }: InsertInput): InsertResult => {
  const current = index >= 0 && index < lines.length ? lines[index] : null
  if (current === null || pasted.length === 0) {
    return { lines, caret: { index, offset } }
  }

  if (current.kind === 'rule') {
    const next = [...lines.slice(0, index + 1), ...pasted.map(cloneLine), ...lines.slice(index + 1)]
    const caretIndex = index + pasted.length
    return { lines: next, caret: { index: caretIndex, offset: runLength(next[caretIndex].runs) } }
  }

  const [left, right] = splitRuns(current.runs, offset)
  const chunks = pasted.map(cloneLine)
  const [first] = chunks
  const [last] = chunks.slice(-1)
  const assembled = assemblePasted({ current, left, right, chunks })
  const next = [...lines.slice(0, index), ...assembled, ...lines.slice(index + 1)]
  return {
    lines: next,
    caret: {
      index: index + assembled.length - 1,
      offset: pastedCaretOffset({ singleText: chunks.length === 1 && first.kind !== 'rule', left, first, last })
    }
  }
}
